import { collectPlaceholders } from "./TemplateChipEditor";
import { PLACEHOLDER_BRACES_LENGTH } from "./TemplatesForm.constants";
import type {
  TemplateApiError,
  TemplateOptionsDto,
  TemplateUnknownVariable,
} from "./TemplatesForm.types";

export interface KnownPaths {
  scalars: Set<string>;
  prefixes: Set<string>;
}

export interface UnknownVariableRange {
  start: number;
  end: number;
  path: string;
  suggestion?: string;
  message: string;
}

export interface ParseErrorRange {
  start: number;
  end: number;
  message?: string;
}

// Проверка валидности пути переменной. params.*/properties.* без конкретного
// имени тоже считаем валидным — имя задаёт юзер, его валидирует бэк. Когда
// whitelist ещё не загружен — не пугаем юзера красным, возвращаем true.
export const buildIsValid =
  (paths: KnownPaths | null): ((raw: string) => boolean) =>
  (raw) => {
    if (!paths) return true;
    if (!raw) return false;
    const lower = raw.toLocaleLowerCase();
    if (paths.scalars.has(lower)) return true;
    const dotIdx = lower.indexOf(".");
    if (dotIdx > 0) {
      const prefix = lower.slice(0, dotIdx);
      const rest = lower.slice(dotIdx + 1);
      if (paths.prefixes.has(prefix) && rest.length > 0) return true;
    }
    return false;
  };

// Заменяет {{targetPath}} на {{suggestion}} через PlaceholderMatch'и — без
// regex.replace(), чтобы избежать regex-injection от метасимволов в path
// (`.`, `(`, `[`) и не зависеть от whitespace внутри {{ }}.
export const applySuggestion = (
  template: string,
  targetPath: string,
  suggestion: string,
): string => {
  const matches = collectPlaceholders(template);
  if (matches.length === 0) return template;
  const replacement = `{{${suggestion}}}`;
  let result = "";
  let cursor = 0;
  for (const m of matches) {
    result += template.slice(cursor, m.start);
    result +=
      m.raw === targetPath ? replacement : template.slice(m.start, m.end);
    cursor = m.end;
  }
  result += template.slice(cursor);
  return result;
};

// Реальный конец `{{...}}` берём из живых матчей шаблона (учитываем whitespace
// внутри скобок и кодировку offset). Fallback на арифметику нужен, если бэк
// прислал offset, а текущий value уже изменился (race с debounce).
export const buildUnknownVariableRanges = (
  template: string,
  unknown: TemplateUnknownVariable[],
): UnknownVariableRange[] => {
  const matches = collectPlaceholders(template);
  return unknown.map((u) => {
    const realMatch = matches.find((m) => m.start === u.offset);
    const end = realMatch
      ? realMatch.end
      : u.offset + u.path.length + PLACEHOLDER_BRACES_LENGTH;
    const message = u.suggestion
      ? `Неизвестная переменная "${u.path}". Возможно, имелось в виду {{${u.suggestion}}}?`
      : `Неизвестная переменная "${u.path}".`;
    return {
      start: u.offset,
      end,
      path: u.path,
      suggestion: u.suggestion,
      message,
    };
  });
};

export const buildParseErrorRanges = (
  error: TemplateApiError | null,
): ParseErrorRange[] => {
  if (error?.kind !== "parse") return [];
  return [
    {
      start: error.offset,
      end: error.end,
      message: error.detail,
    },
  ];
};

// Очистка пустых строк/нулевых maxLength: бэк трактует «явно пустую» fallback
// иначе, чем «отсутствующую», поэтому опциональные поля выкидываем когда
// они равны default. applyOnlyIfEmpty false тоже не отправляем (это дефолт).
export const cleanTemplateOptions = (
  options: TemplateOptionsDto,
): TemplateOptionsDto => {
  const cleaned: TemplateOptionsDto = { template: options.template };
  if (options.fallback && options.fallback.trim().length > 0) {
    cleaned.fallback = options.fallback;
  }
  if (options.applyOnlyIfEmpty) cleaned.applyOnlyIfEmpty = true;
  if (typeof options.maxLength === "number" && options.maxLength > 0) {
    cleaned.maxLength = options.maxLength;
  }
  return cleaned;
};
